/**
 * HORAIRE — Admin /app/horaire
 * CRUD cours + instructeurs + détection chevauchements + archivage + changements futurs.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Warning, CalendarBlank, Users, CircleNotch, Clock } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const RED = "#D90429";
const STATUT_STYLE = { actif: "bg-emerald-500/15 text-emerald-300", futur: "bg-blue-500/15 text-blue-300", archive: "bg-zinc-700 text-zinc-400" };

export default function HoraireAdminModule() {
  const [courses, setCourses] = useState([]);
  const [refs, setRefs] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [overlaps, setOverlaps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("cours");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, i, o] = await Promise.all([
        api.get("/schedule/courses"), api.get("/schedule/refs"),
        api.get("/schedule/instructors"), api.get("/schedule/overlaps"),
      ]);
      setCourses(c.data.courses); setRefs(r.data);
      setInstructors(i.data.instructors); setOverlaps(o.data.overlaps);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function seed() {
    try { const r = await api.post("/schedule/seed"); toast.success(r.data.skipped ? "Horaire déjà présent" : `${r.data.courses_imported} cours importés`); load(); }
    catch { toast.error("Erreur d'import"); }
  }
  async function remove(id) {
    if (!window.confirm("Supprimer ce cours ?")) return;
    await api.delete(`/schedule/courses/${id}`); toast.success("Supprimé"); load();
  }

  if (loading) return <div className="flex justify-center py-32"><CircleNotch size={32} className="animate-spin text-zinc-400" /></div>;

  const byDay = {};
  courses.forEach((c) => { byDay[c.jour] = byDay[c.jour] || []; byDay[c.jour].push(c); });
  const dayOrder = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-8 text-zinc-100" data-testid="horaire-admin">
      <PageHeader
        title="Horaire des cours"
        icon={CalendarBlank}
        breadcrumbs={[{ label: "Dojo & élèves" }, { label: "Horaire" }]}
        subtitle={<>Public : <a href="/horaire" target="_blank" rel="noreferrer" className="underline text-amber-300">/horaire</a></>}
        actions={
          <>
            {courses.length === 0 && <button onClick={seed} data-testid="seed-btn" className="border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 px-4 py-2 rounded-lg text-sm font-semibold text-zinc-200">Importer l'horaire 2026</button>}
            <button onClick={() => setEditing("new")} data-testid="add-course-btn" className="flex items-center gap-2 text-zinc-950 px-5 py-2.5 font-bold rounded-lg bg-amber-500 hover:bg-amber-400 active:scale-[0.98] transition-all"><Plus size={18} weight="bold" /> Ajouter un cours</button>
          </>
        }
      />

      {/* Onglets */}
      <div className="flex gap-2 mb-5 border-b border-zinc-800">
        {[["cours", `Cours (${courses.length})`], ["instructeurs", `Instructeurs (${instructors.length})`], ["chevauchements", `Chevauchements (${overlaps.length})`]].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === id ? "border-amber-500 text-amber-400" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>{lbl}</button>
        ))}
      </div>

      {tab === "cours" && dayOrder.filter((d) => byDay[d]).map((d) => (
        <div key={d} className="mb-6" data-testid={`admin-day-${d}`}>
          <h2 className="font-bold font-cabinet text-lg mb-2 text-zinc-100">{d}</h2>
          <div className="space-y-2">
            {byDay[d].sort((a, b) => a.heure_debut.localeCompare(b.heure_debut)).map((c) => (
              <div key={c.id} className="border border-zinc-800 bg-zinc-900 rounded-lg p-3 flex items-center gap-3 flex-wrap hover:border-zinc-700 transition-colors" data-testid={`admin-course-${c.id}`}>
                <span className="kz-mono text-sm font-bold text-zinc-300 whitespace-nowrap flex items-center gap-1"><Clock size={14} />{c.heure_debut}{c.heure_fin ? `–${c.heure_fin}` : ""}</span>
                <span className="font-semibold flex-1 min-w-[160px] text-zinc-100">{c.titre_du_cours}<span className="text-zinc-500 font-normal text-sm"> · {c.discipline}</span></span>
                <span className="text-xs text-zinc-400">{c.programme}</span>
                <span className="text-xs text-zinc-400">{c.age_min ?? "?"}–{c.age_max ?? "?"} ans</span>
                {c.exception_age && <span className="text-[10px] uppercase bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded">exception âge</span>}
                {c.changement_prevu?.date && <span className="text-[10px] bg-red-500/15 text-amber-300 border border-red-500/40 px-1.5 py-0.5 rounded" data-testid={`admin-change-${c.id}`} title={c.changement_prevu.motif || ""}>↻ dès le {c.changement_prevu.date} : {c.changement_prevu.heure_debut}{c.changement_prevu.heure_fin ? `–${c.changement_prevu.heure_fin}` : ""}</span>}
                <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${STATUT_STYLE[c.statut] || ""}`}>{c.statut}</span>
                {!c.affichage_public && <span className="text-[10px] uppercase bg-zinc-700 text-zinc-400 px-1.5 py-0.5 rounded">privé</span>}
                <button onClick={() => setEditing(c)} data-testid={`edit-${c.id}`} className="text-zinc-500 hover:text-amber-400"><PencilSimple size={18} /></button>
                <button onClick={() => remove(c.id)} data-testid={`del-${c.id}`} className="text-zinc-500 hover:text-red-400"><Trash size={18} /></button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {tab === "instructeurs" && (
        <div className="space-y-2" data-testid="instructors-list">
          {instructors.map((i) => (
            <div key={i.id} className="border border-zinc-800 bg-zinc-900 rounded-lg p-3 flex items-center gap-3">
              <Users size={18} className="text-zinc-500" />
              <span className="font-semibold text-zinc-100">{i.name}</span>
              <span className="text-xs text-zinc-400 flex-1">{i.role}</span>
              {i.lead && <span className="text-[10px] uppercase bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded">instructeur</span>}
            </div>
          ))}
        </div>
      )}

      {tab === "chevauchements" && (
        <div data-testid="overlaps-list">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-200 mb-3 flex items-center gap-2"><Warning size={16} /> Les cours en parallèle sont permis. Cette liste sert au contrôle visuel.</div>
          {overlaps.length === 0 ? <p className="text-zinc-400">Aucun chevauchement.</p> : overlaps.map((o, idx) => (
            <div key={idx} className="border border-zinc-800 bg-zinc-900 rounded-lg p-2.5 text-sm mb-1.5 text-zinc-300"><b className="text-zinc-100">{o.jour}</b> : « {o.a} » ({o.a_h}) ⟷ « {o.b} » ({o.b_h})</div>
          ))}
        </div>
      )}

      {editing && <CourseModal course={editing === "new" ? null : editing} refs={refs} instructors={instructors} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CourseModal({ course, refs, instructors, onClose, onSaved }) {
  const [f, setF] = useState({
    jour: course?.jour || "Lundi", heure_debut: course?.heure_debut || "18:00", heure_fin: course?.heure_fin || "19:00",
    titre_du_cours: course?.titre_du_cours || "", discipline: course?.discipline || (refs?.disciplines?.[0] || ""),
    programme: course?.programme || "", age_min: course?.age_min ?? "", age_max: course?.age_max ?? "",
    niveau_ceinture: course?.niveau_ceinture || "", instructeur_principal: course?.instructeur_principal || "",
    assistants: (course?.assistants || []).join("; "), date_entree_en_vigueur: course?.date_entree_en_vigueur || "",
    date_fin: course?.date_fin || "", statut: course?.statut || "actif",
    affichage_public: course?.affichage_public ?? true, exception_age: course?.exception_age ?? false,
    note_admin: course?.note_admin || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  async function save() {
    if (!f.titre_du_cours.trim()) return toast.error("Titre requis");
    setBusy(true);
    const payload = {
      ...f,
      age_min: f.age_min === "" ? null : parseInt(f.age_min),
      age_max: f.age_max === "" ? null : parseInt(f.age_max),
      assistants: f.assistants.split(";").map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (course) await api.put(`/schedule/courses/${course.id}`, payload);
      else await api.post("/schedule/courses", payload);
      toast.success(course ? "Cours modifié" : "Cours ajouté"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="course-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto">
        <h3 className="text-lg font-bold font-cabinet mb-4">{course ? "Modifier le cours" : "Nouveau cours"}</h3>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Jour"><select value={f.jour} onChange={(e) => set("jour", e.target.value)} data-testid="m-jour" className="hinp">{refs?.jours.map((j) => <option key={j}>{j}</option>)}</select></Fld>
          <Fld label="Statut"><select value={f.statut} onChange={(e) => set("statut", e.target.value)} data-testid="m-statut" className="hinp">{refs?.statuts.map((s) => <option key={s}>{s}</option>)}</select></Fld>
          <Fld label="Heure début"><input type="time" value={f.heure_debut} onChange={(e) => set("heure_debut", e.target.value)} data-testid="m-debut" className="hinp" /></Fld>
          <Fld label="Heure fin"><input type="time" value={f.heure_fin} onChange={(e) => set("heure_fin", e.target.value)} data-testid="m-fin" className="hinp" /></Fld>
          <Fld label="Titre du cours" full><input value={f.titre_du_cours} onChange={(e) => set("titre_du_cours", e.target.value)} data-testid="m-titre" className="hinp" /></Fld>
          <Fld label="Discipline"><select value={f.discipline} onChange={(e) => set("discipline", e.target.value)} data-testid="m-discipline" className="hinp">{refs?.disciplines.map((d) => <option key={d}>{d}</option>)}</select></Fld>
          <Fld label="Programme"><select value={f.programme} onChange={(e) => set("programme", e.target.value)} data-testid="m-programme" className="hinp"><option value="">—</option>{refs?.programmes.map((p) => <option key={p}>{p}</option>)}</select></Fld>
          <Fld label="Âge min"><input type="number" value={f.age_min} onChange={(e) => set("age_min", e.target.value)} data-testid="m-agemin" className="hinp" /></Fld>
          <Fld label="Âge max (99 = et +)"><input type="number" value={f.age_max} onChange={(e) => set("age_max", e.target.value)} data-testid="m-agemax" className="hinp" /></Fld>
          <Fld label="Niveau / ceinture" full><input value={f.niveau_ceinture} onChange={(e) => set("niveau_ceinture", e.target.value)} data-testid="m-niveau" className="hinp" /></Fld>
          <Fld label="Instructeur principal" full>
            <select value={f.instructeur_principal} onChange={(e) => set("instructeur_principal", e.target.value)} data-testid="m-instr" className="hinp"><option value="">—</option>{instructors.map((i) => <option key={i.id}>{i.name}</option>)}</select>
          </Fld>
          <Fld label="Assistants (séparés par ;)" full><input value={f.assistants} onChange={(e) => set("assistants", e.target.value)} data-testid="m-assist" className="hinp" placeholder="Nancy Arpin; Maël Blanchette" /></Fld>
          <Fld label="Date d'entrée en vigueur"><input type="date" value={f.date_entree_en_vigueur} onChange={(e) => set("date_entree_en_vigueur", e.target.value)} data-testid="m-datedeb" className="hinp" /></Fld>
          <Fld label="Date de fin (option)"><input type="date" value={f.date_fin} onChange={(e) => set("date_fin", e.target.value)} data-testid="m-datefin" className="hinp" /></Fld>
          <Fld label="Note admin" full><textarea value={f.note_admin} onChange={(e) => set("note_admin", e.target.value)} rows={2} data-testid="m-note" className="hinp" /></Fld>
        </div>
        <div className="flex gap-4 mt-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.affichage_public} onChange={(e) => set("affichage_public", e.target.checked)} data-testid="m-public" className="w-4 h-4 accent-red-600" /> Affichage public</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.exception_age} onChange={(e) => set("exception_age", e.target.checked)} data-testid="m-exception" className="w-4 h-4 accent-amber-500" /> Exception d'âge</label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={busy} data-testid="m-save" className="flex-1 text-zinc-950 py-2.5 rounded-lg font-bold bg-amber-500 hover:bg-amber-400 disabled:opacity-50 transition-colors">{busy ? "…" : "Enregistrer"}</button>
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 font-semibold">Annuler</button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children, full }) {
  return <label className={`block ${full ? "col-span-2" : ""}`}><span className="text-xs text-zinc-400">{label}</span>{children}</label>;
}
